import {type OnboardingApiService} from 'new/services/OnboardingApiService'
import {type AccountsApiService} from './services/AccountsApiService'
import {type AnalyticsEventService} from './services/analytics/AnalyticsEventService'
import {type GAService} from './services/analytics/GAService'
import {type CapabilitiesApiService} from './services/CapabilitiesApiService'
import {type CapabilityProfilesApiService} from './services/CapabilityProfilesApiService'
import {type EventSubscriptionsApiService} from './services/EventSubscriptionsApiService'
import {type FeatureFlagsApiService} from './services/FeatureFlagsApiService'
import {type FeesApiService} from './services/FeesApiService'
import {type I18nService} from './services/I18nService'
import {type LedgerBalanceApiService} from './services/LedgerBalanceApiService'
import {type LedgerRecordsApiService} from './services/LedgerRecordsApiService'
import {type LocalStorageService} from './services/LocalStorageService'
import {type MfaApiService} from './services/MfaApiService'
import {type OrganisationAddressesApiService} from './services/OrganisationAddressesApiService'
import {type OrganisationFilesApiService} from './services/OrganisationFilesApiService'
import {type OrganisationMetadataApiService} from './services/OrganisationMetadataApiService'
import {type OrganisationsApiService} from './services/OrganisationsApiService'
import {type PartnerIntegrationsApiService} from './services/PartnerIntegrationsApiService'
import {type PaymentAgreementsApiService} from './services/PaymentAgreementsApiService'
import {type PaymentSourcesApiService} from './services/PaymentSourcesApiService'
import {type PayoutsApiService} from './services/PayoutsApiService'
import {type PlanAssignmentsApiService} from './services/PlanAssignmentsApiService'
import {type UserUiPreferencesService} from './services/preferences/UserUiPreferencesService'
import {type RolesAssignmentsApiService} from './services/RollesAssignmentsService'
import {type SolutionFilesApiService} from './services/SolutionFilesApiService'
import {type SolutionsApiService} from './services/SolutionsApiService'
import {type SolutionServicesApiService} from './services/SolutionServicesApiService'
import {type SolutionsTwintQrApiService} from './services/SolutionsTwintQrApiService'
import {type SubscriptionsApiService} from './services/SubscriptionsApiService'
import {type SubscriptionsStatsApiService} from './services/SubscriptionsStatsApiService'
import {type SupporterApiService} from './services/SupporterApiService'
import {type TransactionCurrenciesApiService} from './services/TransactionCurrenciesApiService'
import {type TransactionsApiService} from './services/TransactionsApiService'
import {type TransactionsStatsApiService} from './services/TransactionsStatsApiService'
import {type UsersApiService} from './services/UsersApiService'
import {type WebhookEventsApiService} from './services/WebhookEventsApiService'
import {type WebhooksApiService} from './services/WebhooksApiService'
import {type ApiStore} from './stores/ApiStore'
import {type AppConfigStore} from './stores/AppConfigStore'
import {type CookieConsentStore} from './stores/CookieConsentStore'
import {type CurrentOrganisationAccountsStore} from './stores/CurrentOrganisationAccountsStore'
import {type CurrentOrganisationAddressesStore} from './stores/CurrentOrganisationAddressesStore'
import {type CurrentOrganisationCapabilitiesStore} from './stores/CurrentOrganisationCapabilitiesStore'
import {type CurrentOrganisationCapabilityProfilesStore} from './stores/CurrentOrganisationCapabilityProfilesStore'
import {type CurrentOrganisationFilesStore} from './stores/CurrentOrganisationFilesStore'
import {type CurrentOrganisationFileUploadStore} from './stores/CurrentOrganisationFileUploadStore'
import {type CurrentOrganisationMetadataStore} from './stores/CurrentOrganisationMetadataStore'
import {type CurrentOrganisationPlanAssignmentsStore} from './stores/CurrentOrganisationPlanAssignmentsStore'
import {type CurrentOrganisationStore} from './stores/CurrentOrganisationStore'
import {type CurrentOrganisationUsersStore} from './stores/CurrentOrganisationUsersStore'
import {type CurrentUserStore} from './stores/CurrentUserStore'
import {type EventSubscriptionsStore} from './stores/EventSubsriptionsStore'
import {type FeatureFlagsStore} from './stores/FeatureFlagsStore'
import {type FeesStore} from './stores/FeesStore'
import {type FormChangePasswordStore} from './stores/FormChangePasswordStore'
import {type FormEditSubscription} from './stores/FormEditSubscription'
import {type FormGenericPaylinkButton} from './stores/FormGenericPaylinkButton'
import {type FormLettershopOrderStore} from './stores/FormLettershopOrderStore'
import {type FormMfaStore} from './stores/FormMfaStore'
import {type FormOnboardingStore} from './stores/FormOnboardingStore'
import {type FormRefundStore} from './stores/FormRefundStore'
import {type FormRolesAssignments} from './stores/FormRolesAssignments'
import {type FormSettingsCommunicationStore} from './stores/FormSettingsCommunicationStore'
import {type FormSettingsOrganisationStore} from './stores/FormSettingsOrganisationStore'
import {type FormSettingsOrganisationUserStore} from './stores/FormSettingsOrganisationUserStore'
import {type FormSettingsProfileUserStore} from './stores/FormSettingsProfileUserStore'
import {type FormSolutionConfigStore} from './stores/FormSolutionConfigStore'
import {type FormWebhooksStore} from './stores/FormWebhooksStore'
import {type LedgerBalanceStore} from './stores/LedgerBalanceStore'
import {type OrganisationsStore} from './stores/OrganisationsStore'
import {type PartnerIntegrationsStore} from './stores/PartnerIntegrationsStore'
import {type PaymentAgreementItemStore} from './stores/PaymentAgreementItemStore'
import {type PaymentAgreementsStore} from './stores/PaymentAgreementsStore'
import {type PaymentProvidersStore} from './stores/PaymentProvidersStore'
import {type PayoutItemStore} from './stores/PayoutItemStore'
import {type PayoutLedgerRecordsStore} from './stores/PayoutLedgerRecordsStore'
import {type PayoutsStore} from './stores/PayoutsStore'
import {type PayoutTransactionsStore} from './stores/PayoutTransactionsStore'
import {type RolesAssignmentStore} from './stores/RolesAssignmentStore'
import {type RouterStore} from './stores/RouterStore'
import {type SessionStore} from './stores/SessionStore'
import {type SolutionFileUploadStore} from './stores/SolutionFileUploadStore'
import {type SolutionItemStore} from './stores/SolutionItemStore'
import {type SolutionPaymentMethodsModalStore} from './stores/SolutionPaymentMethodsModalStore'
import {type SolutionRequiredSupporterFieldsStore} from './stores/SolutionRequiredSupporterFieldsStore'
import {type SolutionServicesStore} from './stores/SolutionServicesStore'
import {type SolutionShownSupporterFieldsStore} from './stores/SolutionShownSupporterFieldsStore'
import {type SolutionsStore} from './stores/SolutionsStore'
import {type SubscriptionInvoiceItemStore} from './stores/SubscriptionInvoiceItemStore'
import {type SubscriptionInvoicesStore} from './stores/SubscriptionInvoicesStore'
import {type SubscriptionItemStore} from './stores/SubscriptionItemStore'
import {type SubscriptionsStatsStore} from './stores/SubscriptionsStatsStore'
import {type SubscriptionsStore} from './stores/SubscriptionsStore'
import {type ThemeStore} from './stores/ThemeStore'
import {type TransactionCurrenciesStore} from './stores/TransactionCurrenciesStore'
import {type TransactionItemStore} from './stores/TransactionItemStore'
import {type TransactionsStatsStore} from './stores/TransactionsStatsStore'
import {type TransactionsStore} from './stores/TransactionsStore'
import {type UiStore} from './stores/UiStore'
import {type UserPreferencesStore} from './stores/UserPreferencesStore'
import {type WebhookEventsStore} from './stores/WebhookEventsStore'
import {type WebhooksStore} from './stores/WebhooksStore'
import {type ZendeskWidgetStore} from './stores/ZendeskWidgetStore'

///////////////////////////////////////////////////////////////////////////////

export type Ctx = Partial<{
  // services
  accountsApiService: AccountsApiService
  analyticsEventService: AnalyticsEventService
  capabilitiesApiService: CapabilitiesApiService
  capabilityProfilesApiService: CapabilityProfilesApiService
  eventSubscriptionsApiService: EventSubscriptionsApiService
  featureFlagsApiService: FeatureFlagsApiService
  feesApiService: FeesApiService
  gaService: GAService
  i18nService: I18nService
  ledgerBalanceApiService: LedgerBalanceApiService
  ledgerRecordsApiService: LedgerRecordsApiService
  localStorageService: LocalStorageService
  mfaApiService: MfaApiService
  onboardingApiService: OnboardingApiService
  organisationAddressesApiService: OrganisationAddressesApiService
  organisationFilesApiService: OrganisationFilesApiService
  organisationMetadataApiService: OrganisationMetadataApiService
  organisationsApiService: OrganisationsApiService
  partnerintegrationsApiService: PartnerIntegrationsApiService
  paymentSourcesApiService: PaymentSourcesApiService
  payoutsApiService: PayoutsApiService
  planAssignmentsApiService: PlanAssignmentsApiService
  rolesAssignmentsApiService: RolesAssignmentsApiService
  solutionFilesApiService: SolutionFilesApiService
  solutionsApiService: SolutionsApiService
  solutionServicesApiService: SolutionServicesApiService
  solutionsTwintQrApiService: SolutionsTwintQrApiService
  subscriptionsApiService: SubscriptionsApiService
  subscriptionsStatsApiService: SubscriptionsStatsApiService
  transactionCurrenciesApiService: TransactionCurrenciesApiService
  transactionsApiService: TransactionsApiService
  transactionsStatsApiService: TransactionsStatsApiService
  paymentAgreementsApiService: PaymentAgreementsApiService
  usersApiService: UsersApiService
  userUiPreferencesService: UserUiPreferencesService
  supporterApiService: SupporterApiService
  webhookEventsApiService: WebhookEventsApiService
  webhooksApiService: WebhooksApiService

  // stores
  apiStore: ApiStore
  appConfigStore: AppConfigStore
  cookieConsentStore: CookieConsentStore
  currentOrganisationAddressesStore: CurrentOrganisationAddressesStore
  currentOrganisationAccountsStore: CurrentOrganisationAccountsStore
  currentOrganisationCapabilitiesStore: CurrentOrganisationCapabilitiesStore
  currentOrganisationCapabilityProfilesStore: CurrentOrganisationCapabilityProfilesStore
  currentOrganisationFilesStore: CurrentOrganisationFilesStore
  currentOrganisationFileUploadStore: CurrentOrganisationFileUploadStore
  currentOrganisationMetadataStore: CurrentOrganisationMetadataStore
  currentOrganisationPlanAssignmentsStore: CurrentOrganisationPlanAssignmentsStore
  currentOrganisationStore: CurrentOrganisationStore
  currentOrganisationUsersStore: CurrentOrganisationUsersStore
  currentUserStore: CurrentUserStore
  eventSubscriptionsStore: EventSubscriptionsStore
  featureFlagsStore: FeatureFlagsStore
  feesStore: FeesStore
  ledgerBalanceStore: LedgerBalanceStore
  organisationsStore: OrganisationsStore
  partnerIntegrationsStore: PartnerIntegrationsStore
  paymentProvidersStore: PaymentProvidersStore
  payoutItemStore: PayoutItemStore
  payoutLedgerRecordsStore: PayoutLedgerRecordsStore
  payoutTransactionsStore: PayoutTransactionsStore
  payoutsStore: PayoutsStore
  routerStore: RouterStore
  rolesAssignmentStore: RolesAssignmentStore
  sessionStore: SessionStore
  solutionItemStore: SolutionItemStore
  solutionPaymentMethodsModalStore: SolutionPaymentMethodsModalStore
  solutionRequiredSupporterFieldsStore: SolutionRequiredSupporterFieldsStore
  solutionServicesStore: SolutionServicesStore
  solutionShownSupporterFieldsStore: SolutionShownSupporterFieldsStore
  solutionFileUploadStore_Info: SolutionFileUploadStore
  solutionFileUploadStore_Logo: SolutionFileUploadStore
  solutionsStore: SolutionsStore
  subscriptionInvoiceItemStore: SubscriptionInvoiceItemStore
  subscriptionInvoicesStore: SubscriptionInvoicesStore
  subscriptionItemStore: SubscriptionItemStore
  subscriptionsStore: SubscriptionsStore
  subscriptionsStatsStore: SubscriptionsStatsStore
  themeStore: ThemeStore
  transactionCurrenciesStore: TransactionCurrenciesStore
  transactionItemStore: TransactionItemStore
  paymentAgreementItemStore: PaymentAgreementItemStore
  transactionsStatsStore: TransactionsStatsStore
  transactionsStore: TransactionsStore
  paymentAgreementsStore: PaymentAgreementsStore
  uiStore: UiStore
  userPreferencesStore: UserPreferencesStore
  zendeskWidgetStore: ZendeskWidgetStore
  webhookEventsStore: WebhookEventsStore
  webhooksStore: WebhooksStore

  // forms
  formChangePasswordStore: FormChangePasswordStore
  formEditSubscription: FormEditSubscription
  formLettershopOrderStore: FormLettershopOrderStore
  formMfaStore: FormMfaStore
  formOnboardingStore: FormOnboardingStore
  formRolesAssignments: FormRolesAssignments
  formRefundStore: FormRefundStore
  formSettingsOrganisationStore: FormSettingsOrganisationStore
  formSettingsOrganisationUserStore: FormSettingsOrganisationUserStore
  formSettingsProfileUserStore: FormSettingsProfileUserStore
  formSolutionConfigStore: FormSolutionConfigStore
  formGenericPaylinkButton: FormGenericPaylinkButton
  formSettingsCommunicationStore: FormSettingsCommunicationStore
  formWebhooksStore: FormWebhooksStore
}>

///////////////////////////////////////////////////////////////////////////////

export const ctx: Ctx = {}

//
;(window as any).ctx = ctx
